/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any, import/prefer-default-export, prefer-arrow/prefer-arrow-functions -- Legacy store setup code */

/**
 * This file is where we do "rehydration" of your RootStore from AsyncStorage.
 * This lets you persist your state between app launches.
 *
 * Note that Fast Refresh doesn't play well with this file, so if you edit this,
 * do a full refresh of your app instead.
 *
 * @refresh reset
 */
import type { IDisposer } from 'mobx-state-tree';
import { applySnapshot, onSnapshot } from 'mobx-state-tree';

import type { IRootModel } from 'watchtower-ui/models/Root';
import { loadStore, saveStore } from 'watchtower-ui/storage';

/**
 * The key we'll be saving our state as within async storage.
 */
const ROOT_STATE_STORAGE_KEY = 'watchtower-v1';

/**
 * Setup the root state.
 */
let _disposer: IDisposer | undefined;
export async function setupRootStore(rootStore: IRootModel) {
  let restoredState: any;

  try {
    // load the last known state from AsyncStorage
    restoredState = (await loadStore(ROOT_STATE_STORAGE_KEY)) || {};
    applySnapshot(rootStore, restoredState);
  } catch {
    // if there's any problems loading, then silently continue
  }

  // stop tracking state changes if we've already setup
  if (_disposer) _disposer();

  // track changes & save to AsyncStorage
  _disposer = onSnapshot(rootStore, (snapshot) => saveStore(ROOT_STATE_STORAGE_KEY, snapshot));

  const unsubscribe = () => {
    if (_disposer) _disposer();
    _disposer = undefined;
  };

  return { rootStore, restoredState, unsubscribe };
}
